import { API_ENDPOINTS } from '../../config/api';
import type { CarDetail, CarListItem, CarListResponse, CarListingStatus, CarSort } from './carInventoryTypes';

export interface CarListQuery {
  page?: number;
  limit?: number;
  q?: string;
  make?: string;
  model?: string;
  yearMin?: string;
  yearMax?: string;
  bodyStyle?: string;
  sort?: CarSort;
  status?: CarListingStatus;
}

export class CarNotFoundError extends Error {}

function uniqueCars(cars: CarListItem[]): CarListItem[] {
  const seen = new Set<string>();
  return cars.filter((car) => {
    if (seen.has(car.id)) return false;
    seen.add(car.id);
    return true;
  });
}

function mergeStringMap(
  a: Record<string, string[]> | undefined,
  b: Record<string, string[]> | undefined
): Record<string, string[]> {
  const out: Record<string, string[]> = { ...(a ?? {}) };
  for (const [key, values] of Object.entries(b ?? {})) {
    out[key] = [...new Set([...(out[key] ?? []), ...values].filter(Boolean))].sort((x, y) => x.localeCompare(y));
  }
  return out;
}

function mergeYearMap(
  a: Record<string, number[]> | undefined,
  b: Record<string, number[]> | undefined
): Record<string, number[]> {
  const out: Record<string, number[]> = { ...(a ?? {}) };
  for (const [key, values] of Object.entries(b ?? {})) {
    out[key] = [...new Set([...(out[key] ?? []), ...values].filter((year) => typeof year === 'number'))].sort(
      (x, y) => y - x
    );
  }
  return out;
}

export function mergeFacets(a: CarListResponse['facets'], b: CarListResponse['facets']): CarListResponse['facets'] {
  const uniq = (values: string[]) => [...new Set(values.filter(Boolean))].sort((x, y) => x.localeCompare(y));
  return {
    makes: uniq([...(a?.makes ?? []), ...(b?.makes ?? [])]),
    models: uniq([...(a?.models ?? []), ...(b?.models ?? [])]),
    years: [...new Set([...(a?.years ?? []), ...(b?.years ?? [])])]
      .filter((year) => typeof year === 'number' && Number.isFinite(year))
      .sort((x, y) => y - x),
    body_styles: uniq([...(a?.body_styles ?? []), ...(b?.body_styles ?? [])]),
    models_by_make: mergeStringMap(a?.models_by_make, b?.models_by_make),
    years_by_make: mergeYearMap(a?.years_by_make, b?.years_by_make),
  };
}

export function harvestMapsFromCars(cars: CarListItem[]): CarListResponse['facets'] {
  const modelsByMake: Record<string, string[]> = {};
  const yearsByMake: Record<string, number[]> = {};
  for (const car of cars) {
    const makeName = (car.make || '').trim();
    if (!makeName) continue;
    if (car.model) {
      const models = modelsByMake[makeName] ?? [];
      if (!models.includes(car.model)) models.push(car.model);
      modelsByMake[makeName] = models;
    }
    if (typeof car.year === 'number' && Number.isFinite(car.year)) {
      const years = yearsByMake[makeName] ?? [];
      if (!years.includes(car.year)) years.push(car.year);
      yearsByMake[makeName] = years;
    }
  }
  const makes = Object.keys(modelsByMake).sort((a, b) => a.localeCompare(b));
  const models = [...new Set(Object.values(modelsByMake).flat())].sort((a, b) => a.localeCompare(b));
  const years = [...new Set(Object.values(yearsByMake).flat())].sort((a, b) => b - a);
  for (const makeName of makes) {
    modelsByMake[makeName].sort((a, b) => a.localeCompare(b));
    yearsByMake[makeName]?.sort((a, b) => b - a);
  }
  return {
    makes,
    models,
    years,
    body_styles: [],
    models_by_make: modelsByMake,
    years_by_make: yearsByMake,
  };
}

export interface CarCatalogFilters {
  q?: string;
  make?: string;
  models?: string[];
  yearMin?: string;
  yearMax?: string;
  kmMin?: string;
  kmMax?: string;
  priceMin?: string;
  priceMax?: string;
  bodyStyle?: string;
}

/** Apply make / model / year / body / search locally so the grid matches the ticks even if the API ignores them. */
export function filterCarCatalog(cars: CarListItem[], filters: CarCatalogFilters): CarListItem[] {
  const qWords = (filters.q || '')
    .trim()
    .toLowerCase()
    .replace(/^#/, '')
    .split(/\s+/)
    .filter(Boolean);
  const make = (filters.make || '').trim().toLowerCase();
  const models = (filters.models || []).map((value) => value.trim().toLowerCase()).filter(Boolean);
  const body = (filters.bodyStyle || '').trim().toLowerCase();
  let yearMin = filters.yearMin ? Number(filters.yearMin) : null;
  let yearMax = filters.yearMax ? Number(filters.yearMax) : null;
  if (yearMin != null && !Number.isFinite(yearMin)) yearMin = null;
  if (yearMax != null && !Number.isFinite(yearMax)) yearMax = null;
  if (yearMin != null && yearMax != null && yearMin > yearMax) {
    const swapped = yearMin;
    yearMin = yearMax;
    yearMax = swapped;
  }
  let kmMin = filters.kmMin ? Number(filters.kmMin) : null;
  let kmMax = filters.kmMax ? Number(filters.kmMax) : null;
  if (kmMin != null && !Number.isFinite(kmMin)) kmMin = null;
  if (kmMax != null && !Number.isFinite(kmMax)) kmMax = null;
  if (kmMin != null && kmMax != null && kmMin > kmMax) {
    const swapped = kmMin;
    kmMin = kmMax;
    kmMax = swapped;
  }
  let priceMin = filters.priceMin ? Number(filters.priceMin) : null;
  let priceMax = filters.priceMax ? Number(filters.priceMax) : null;
  if (priceMin != null && !Number.isFinite(priceMin)) priceMin = null;
  if (priceMax != null && !Number.isFinite(priceMax)) priceMax = null;
  if (priceMin != null && priceMax != null && priceMin > priceMax) {
    const swapped = priceMin;
    priceMin = priceMax;
    priceMax = swapped;
  }

  return cars.filter((car) => {
    const carMake = (car.make || '').toLowerCase();
    const carModel = (car.model || '').toLowerCase();
    const carBody = (car.body_style || '').toLowerCase();
    if (make && carMake !== make) return false;
    if (models.length && !models.includes(carModel)) return false;
    if (body && carBody !== body) return false;
    if (yearMin != null && (car.year == null || car.year < yearMin)) return false;
    if (yearMax != null && (car.year == null || car.year > yearMax)) return false;
    if (kmMin != null && (car.odometer_km == null || car.odometer_km < kmMin)) return false;
    if (kmMax != null && (car.odometer_km == null || car.odometer_km > kmMax)) return false;
    if (priceMin != null && (car.price == null || car.price < priceMin)) return false;
    if (priceMax != null && (car.price == null || car.price > priceMax)) return false;
    if (qWords.length) {
      const hay = [car.year, car.make, car.model, car.trim, car.stock_number, car.id]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!qWords.every((word) => hay.includes(word.replace(/^#/, '')))) return false;
    }
    return true;
  });
}

type CatalogPayload = {
  cars: CarListItem[];
  facets: CarListResponse['facets'];
  counts: CarListResponse['counts'];
};

const catalogCache = new Map<string, { at: number; data: CatalogPayload }>();
const CATALOG_CACHE_MS = 2 * 60 * 1000;

function cacheEnabled(): boolean {
  return import.meta.env.MODE !== 'test';
}

function sliceCatalog(all: CatalogPayload, status: CarListingStatus): CatalogPayload {
  if (status === 'all') return all;
  return {
    ...all,
    cars: all.cars.filter((car) => (status === 'sold' ? car.is_sold : !car.is_sold)),
  };
}

export async function fetchInventoryCatalog(
  status: CarListingStatus,
  signal?: AbortSignal,
  onProgress?: (partial: CatalogPayload) => void
): Promise<CatalogPayload> {
  if (cacheEnabled()) {
    const allHit = catalogCache.get('all');
    if (allHit && Date.now() - allHit.at < CATALOG_CACHE_MS) {
      return sliceCatalog(allHit.data, status);
    }
    const hit = catalogCache.get(status);
    if (hit && Date.now() - hit.at < CATALOG_CACHE_MS) return hit.data;
  }

  const statuses: CarListingStatus[] = status === 'all' ? ['for_sale', 'sold'] : [status];
  const cars: CarListItem[] = [];
  let maps: CarListResponse['facets'] = {
    makes: [],
    models: [],
    years: [],
    body_styles: [],
    models_by_make: {},
    years_by_make: {},
  };
  let counts: CarListResponse['counts'] = { for_sale: 0, sold: 0, all: 0 };

  for (const listingStatus of statuses) {
    let page = 1;
    let loadedForStatus = 0;
    while (page <= 20) {
      const data = await rawFetchCarList({ status: listingStatus, page, limit: 100 }, signal);
      const batch = data.cars ?? [];
      cars.push(...batch);
      loadedForStatus += batch.length;
      maps = mergeFacets(maps, data.facets);
      maps = mergeFacets(maps, harvestMapsFromCars(batch));
      if (data.counts) counts = data.counts;
      const hasMore =
        data.has_more ||
        (typeof data.total === 'number' && loadedForStatus < data.total && batch.length > 0);
      if (!hasMore) break;
      page += 1;
    }
    if (status === 'all' && listingStatus === 'for_sale') {
      onProgress?.({ cars: uniqueCars(cars), facets: maps, counts });
    }
  }

  const result = { cars: uniqueCars(cars), facets: maps, counts };
  if (cacheEnabled()) {
    catalogCache.set(status, { at: Date.now(), data: result });
    if (status === 'all') catalogCache.set('all', { at: Date.now(), data: result });
  }
  return result;
}

async function rawFetchCarList(query: CarListQuery, signal?: AbortSignal): Promise<CarListResponse> {
  const url = new URL(API_ENDPOINTS.CAR_INVENTORY_PUBLIC);
  if (query.page) url.searchParams.set('page', String(query.page));
  if (query.limit) url.searchParams.set('limit', String(query.limit));
  if (query.q) url.searchParams.set('q', query.q);
  if (query.make) url.searchParams.set('make', query.make);
  if (query.model) url.searchParams.set('model', query.model);
  if (query.yearMin) url.searchParams.set('year_min', query.yearMin);
  if (query.yearMax) url.searchParams.set('year_max', query.yearMax);
  if (query.bodyStyle) url.searchParams.set('body_style', query.bodyStyle);
  if (query.sort) url.searchParams.set('sort', query.sort);
  if (query.status) url.searchParams.set('status', query.status);

  const res = await fetch(url.toString(), { signal });
  if (!res.ok) throw new Error(`Failed to load vehicles (${res.status})`);
  return res.json();
}

/** All tab: for-sale first, then sold. Does not rely on the API's mixed-list sort. */
async function fetchAllForSaleFirst(query: CarListQuery, signal?: AbortSignal): Promise<CarListResponse> {
  const page = query.page ?? 1;
  const limit = query.limit ?? 25;
  const skip = (page - 1) * limit;

  const sale = await rawFetchCarList({ ...query, status: 'for_sale', page, limit }, signal);
  const forSaleTotal = sale.total;

  if (skip < forSaleTotal) {
    if (sale.cars.length === limit) {
      const soldProbe = await rawFetchCarList({ ...query, status: 'sold', page: 1, limit: 1 }, signal);
      const total = forSaleTotal + soldProbe.total;
      return {
        ...sale,
        status: 'all',
        total,
        has_more: skip + sale.cars.length < total,
        facets: mergeFacets(sale.facets, soldProbe.facets),
        counts: sale.counts ?? soldProbe.counts,
      };
    }
    const need = limit - sale.cars.length;
    const sold = await rawFetchCarList({ ...query, status: 'sold', page: 1, limit: Math.max(need, 1) }, signal);
    const cars = uniqueCars([...sale.cars, ...sold.cars]).slice(0, limit);
    const total = forSaleTotal + sold.total;
    return {
      cars,
      total,
      page,
      limit,
      has_more: skip + cars.length < total,
      status: 'all',
      counts: sale.counts ?? sold.counts,
      facets: mergeFacets(sale.facets, sold.facets),
    };
  }

  const soldSkip = skip - forSaleTotal;
  const soldPage = Math.floor(soldSkip / limit) + 1;
  const offset = soldSkip % limit;
  const soldA = await rawFetchCarList({ ...query, status: 'sold', page: soldPage, limit }, signal);
  let cars = soldA.cars.slice(offset);
  if (cars.length < limit) {
    const soldB = await rawFetchCarList({ ...query, status: 'sold', page: soldPage + 1, limit }, signal);
    cars = uniqueCars([...cars, ...soldB.cars]).slice(0, limit);
  }
  const total = forSaleTotal + soldA.total;
  return {
    cars,
    total,
    page,
    limit,
    has_more: skip + cars.length < total,
    status: 'all',
    counts: soldA.counts,
    facets: soldA.facets,
  };
}

export async function fetchCarList(query: CarListQuery, signal?: AbortSignal): Promise<CarListResponse> {
  if ((query.status ?? 'all') === 'all') {
    return fetchAllForSaleFirst(query, signal);
  }
  return rawFetchCarList(query, signal);
}

export async function fetchCarDetail(vehicleId: string, signal?: AbortSignal): Promise<CarDetail> {
  const res = await fetch(API_ENDPOINTS.CAR_INVENTORY_BY_ID(vehicleId), { signal });
  if (res.status === 404 || res.status === 400) throw new CarNotFoundError('Vehicle not found');
  if (!res.ok) throw new Error(`Failed to load vehicle (${res.status})`);
  const data = await res.json();
  return data.car;
}
